<template>
  <v-container>
    <!-- Full filter surface (matches photo-mode tagger views).
         Count-mode tenants never apply tags, so `tags` stays empty and
         `onlyUntagged` is off — the backend `apply_group_filters` ignores both. -->
    <v-row class="mb-4">
      <v-col cols="12">
        <PhotoFilter :initial-filter="groupFilters" @apply-filters="setFilters($event)"></PhotoFilter>
      </v-col>
    </v-row>

    <v-row v-if="!currentGroup">
      <v-col cols="12" class="text-center">
        <v-progress-circular v-if="isLoading" indeterminate></v-progress-circular>
        <div v-else>
          <h3>No groups available for counting</h3>
          <v-btn color="primary" class="mt-4" @click="getNextGroup">
            Check for New Groups
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <template v-if="currentGroup">
      <!-- Group Info -->
      <v-row>
        <v-col cols="12">
          <h3>
            Group: {{ currentGroup.response_group_id }} / Question: {{ currentGroup.question_id }}
            <span class="text-caption ml-4">({{ currentGroup.photos.length }} photos)</span>
          </h3>
        </v-col>
      </v-row>

      <!-- Horizontal Photo Gallery -->
      <div class="photo-gallery-container">
        <div class="photo-gallery">
          <div v-for="photo in currentGroup.photos" :key="photo.id" class="photo-item">
            <image-zoom
              :regular="photo.image_url"
              :zoom="photo.image_url"
              :zoom-amount="4"
              :click-zoom="true"
              img-class="photo-image"
            />
            <div class="photo-id text-caption text-center">ID: {{ photo.response_id }}</div>
          </div>
        </div>
      </div>

      <!-- Count Panel -->
      <v-row class="border--primary mt-4">
        <v-col cols="12">
          <CountPanel
            :photo="currentGroup"
            :isQc="false"
            :isGroup="true"
            @saved="onCountSaved"
          />
        </v-col>
      </v-row>

      <!-- Submit Buttons -->
      <v-row class="border--primary mb-2 mt-10">
        <v-btn class="mr-2" color="success" @click="submitGroup({getNext: true})">
          Submit & Get Next Group
        </v-btn>
        <v-btn class="mr-2" color="primary" @click="submitGroup({getNext: false})">
          Submit & Close
        </v-btn>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import imageZoom from 'vue-image-zoomer'
import PhotoFilter from '@/components/filters/PhotoFilter'
import CountPanel from '@/components/counts/CountPanel'

export default {
  name: 'GroupCounting',
  components: { PhotoFilter, CountPanel, imageZoom },
  data() {
    return {
      // PhotoFilter-shaped object; keys the backend doesn't read on the
      // group endpoint (tags, onlyUntagged, includePartiallyTagged,
      // includeInactive, country) are kept for shape-compat with the
      // shared component.
      groupFilters: {
        fromDt: null,
        thruDt: null,
        tags: [],
        projects: [],
        jobs: [],
        onlyUntagged: false,
        includePartiallyTagged: true,
        includeInactive: false,
        respIdSearchFreeForm: null,
        country: null,
        retailer: null,
        department: null,
      },
      isLoading: false,
    }
  },
  mounted() {
    this.getCountItems()
    this.getNextGroup()
  },
  computed: {
    ...mapState({
      currentGroup: state => state.photoGroups.currentGroup,
      groups: state => state.photoGroups.groups,
    })
  },
  methods: {
    ...mapActions(['fetchGroups', 'submitAssignedGroups', 'getCountItems']),
    ...mapMutations(['setMessage', 'setCurrentGroup', 'setGroups']),

    async getNextGroup(payload = null) {
      this.isLoading = true
      try {
        await this.fetchGroups(payload ?? this.groupFilters)
      } finally {
        this.isLoading = false
      }
    },
    setFilters(newFilter) {
      this.groupFilters = newFilter
      this.getNextGroup(this.groupFilters)
    },
    onCountSaved() {
      this.setMessage({ text: 'Counts Saved', type: 'success', alert: true })
    },
    async submitGroup(payload) {
      // IT-699: submitAssignedGroups used to swallow its own failure, so this
      // reported success no matter what happened. Now that it reports, handle
      // it here rather than letting a rejection escape the click handler.
      try {
        await this.submitAssignedGroups()
      } catch (e) {
        this.setMessage({
          text: 'Could not submit this group — please try again.',
          type: 'error',
          alert: true,
        })
        return
      }

      this.setMessage({ text: 'Group Submitted', type: 'success', alert: true })

      if (payload.getNext) {
        this.getNextGroup(this.groupFilters)
      } else {
        this.setGroups([])
        this.setCurrentGroup(null)
      }
    },
  }
}
</script>

<style scoped>
.border--primary {
  border: 1px solid #666;
  border-radius: 4px;
  padding: 10px;
}
.photo-gallery-container {
  overflow-x: auto;
}
.photo-gallery {
  display: flex;
  flex-direction: row;
  gap: 0;
  padding: 0;
}
.photo-item {
  flex: 0 0 auto;
}
.photo-id {
  margin-top: 0;
  color: #666;
}
</style>

<!--
  vue-image-zoomer renders its own <img> inside a child component;
  scoped CSS's [data-v-XXXX] attribute selector doesn't reach it, so
  .photo-image rules only bind when this block is UNSCOPED. Same pattern
  PhotoCounting.vue uses for .count-image.
-->
<style>
.photo-image {
  cursor: pointer;
  max-height: 60vh;
  height: auto;
  width: auto;
  max-width: 100%;
  display: block;
}
.photo-image:hover {
  outline: 2px solid var(--v-primary-base);
}
</style>
